import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  MapPin,
  Clock,
  BookOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import CookieBanner from "@/components/CookieBanner";

const WHATSAPP_URL = "https://wa.me/34672972514?text=Hola%2C%20he%20visto%20tu%20web%20y%20me%20interesa%20contratar%20tus%20servicios";
const LOGO_URL = "/datyon-logo.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
};

const articles = [
  {
    slug: "problema-stock-rentabilidad-empresas",
    title: "El problema del stock que está afectando a la rentabilidad de muchas empresas (y casi nadie ve)",
    excerpt: "Hay empresas que venden, facturan y tienen movimiento, pero arrastran un problema silencioso que impacta directamente en su dinero: el stock. Inventario que no cuadra, datos sucios y rentabilidad bloqueada.",
    readTime: "6 min",
    date: "2026-05-04",
    tag: "Stock e inventario"
  },
  {
    slug: "que-kpis-medir-empresa-rentable",
    title: "Qué KPIs debe medir una empresa para ser rentable",
    excerpt: "Facturar no es lo mismo que ganar dinero. Los 6 KPIs clave que toda empresa debería medir para saber si es rentable de verdad: margen, CAC, ticket medio, rentabilidad por producto, costes y flujo de caja.",
    readTime: "6 min",
    date: "2026-05-01",
    tag: "KPIs y rentabilidad"
  },
  {
    slug: "como-saber-si-tu-empresa-pierde-dinero",
    title: "Cómo saber si tu empresa está perdiendo dinero (y no te estás dando cuenta)",
    excerpt: "Tu empresa puede estar perdiendo dinero y probablemente no lo sabes. No es una exageración. Es algo que ocurre en muchas empresas, incluso en las que aparentemente funcionan bien.",
    readTime: "5 min",
    date: "2026-04-15",
    tag: "Estrategia de datos"
  }
];

export default function Blog() {
  useEffect(() => {
    document.title = "Blog de Análisis de Datos y BI | Datyon";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Artículos sobre análisis de datos, business intelligence y toma de decisiones basada en datos para empresas.");
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://datyon.com/blog");
    }
    window.scrollTo(0, 0);
    return () => {
      document.title = "Análisis de Datos en Murcia para Empresas | Datyon";
      if (metaDesc) metaDesc.setAttribute("content", "Transforma los datos de tu empresa en decisiones rentables. Servicios de análisis de datos, business intelligence y modelos predictivos en Murcia.");
      if (canonical) canonical.setAttribute("href", "https://datyon.com/");
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#020617] relative overflow-x-hidden" data-testid="blog-page">
      <div className="noise-overlay" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="blog-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center pl-4" data-testid="blog-nav-logo">
              <img src={LOGO_URL} alt="DATYON - Análisis de Datos" className="h-20 w-auto" style={{ maxHeight: '80px' }} />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Inicio</Link>
              <Link to="/servicios" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Servicios</Link>
              <Link to="/proceso" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Proceso</Link>
              <Link to="/sobre-mi" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Sobre mí</Link>
              <Link to="/blog" className="nav-link text-indigo-400 hover:text-white text-sm font-medium transition-colors">Blog</Link>
              <Link to="/contacto" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Contacto</Link>
            </div>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero */}
        <section className="relative section-padding pt-32 overflow-hidden" data-testid="blog-hero">
          <div className="spotlight-1 -top-40 -left-40" />
          <div className="spotlight-2 top-1/3 right-0" />

          <div className="max-w-4xl mx-auto relative z-10">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
              <motion.p variants={fadeInUp} className="text-indigo-400 font-medium mb-4 text-sm md:text-base flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Blog
              </motion.p>
              <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl font-extrabold leading-tight mb-6">
                Ideas y reflexiones sobre{" "}
                <span className="text-gradient">datos y negocio</span>
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-slate-400 text-base md:text-lg max-w-2xl">
                Artículos prácticos sobre cómo usar los datos para tomar mejores decisiones en tu empresa.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="relative px-6 pb-24" data-testid="blog-articles-list">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="space-y-6"
            >
              {articles.map((article) => (
                <motion.div key={article.slug} variants={fadeInUp}>
                  <Link to={`/blog/${article.slug}`} className="block group" data-testid={`blog-card-${article.slug}`}>
                    <Card className="bg-slate-900/60 border-slate-800 hover:border-indigo-500/50 transition-all duration-300 group-hover:-translate-y-1">
                      <CardContent className="p-6 sm:p-8">
                        <div className="flex flex-wrap items-center gap-3 mb-4">
                          <span className="text-xs font-medium text-indigo-400 bg-indigo-400/10 px-3 py-1 rounded-full">
                            {article.tag}
                          </span>
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {article.readTime} de lectura
                          </span>
                          <span className="text-xs text-slate-500">
                            {new Date(article.date).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </span>
                        </div>
                        <h2 className="text-xl sm:text-2xl font-bold text-white mb-3 group-hover:text-indigo-400 transition-colors">
                          {article.title}
                        </h2>
                        <p className="text-slate-400 text-sm sm:text-base mb-4 leading-relaxed">
                          {article.excerpt}
                        </p>
                        <span className="inline-flex items-center text-indigo-400 text-sm font-medium group-hover:gap-3 gap-2 transition-all">
                          Leer artículo
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative section-padding bg-slate-900/50" data-testid="blog-cta">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.h2 variants={fadeInUp} className="text-2xl sm:text-3xl font-bold mb-4">
                ¿Quieres saber qué dicen <span className="text-gradient">tus datos</span>?
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-slate-400 mb-8 text-base">
                Si crees que tu empresa puede tomar mejores decisiones con datos, hablemos.
              </motion.p>
              <motion.div variants={fadeInUp}>
                <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base rounded-full">
                  <Link to="/contacto" data-testid="blog-cta-contact">
                    Solicita tu auditoría gratuita
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-800" data-testid="blog-footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="DATYON" className="h-8 w-auto" />
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <span className="text-sm">Murcia, España</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-500 hover:text-white text-sm transition-colors">Inicio</Link>
              <Link to="/servicios" className="text-slate-500 hover:text-white text-sm transition-colors">Servicios</Link>
              <Link to="/blog" className="text-slate-500 hover:text-white text-sm transition-colors">Blog</Link>
              <Link to="/contacto" className="text-slate-500 hover:text-white text-sm transition-colors">Contacto</Link>
            </div>
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-4 mb-6 pt-4 border-t border-slate-800">
            <Link to="/aviso-legal" className="text-slate-500 hover:text-white text-sm transition-colors">Aviso Legal</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-privacidad" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Privacidad</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-cookies" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Cookies</Link>
          </div>
          <p className="text-slate-500 text-sm text-center md:text-left">
            © {new Date().getFullYear()} DATYON - Antonio Oliver. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* WhatsApp Floating */}
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg shadow-green-500/30 transition-all duration-300 hover:scale-110 group"
        data-testid="blog-whatsapp-float" aria-label="Contactar por WhatsApp">
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-slate-800 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          ¿Hablamos?
        </span>
      </a>

      <CookieBanner />
    </div>
  );
}
